/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Destination rules", description = "Destination rules allow organizations to filter out users from destinations they have not consented to during during syncing.", lastUpdatedDate = new Date(1715644800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Destination rules",
        description: "Destination rules allow organizations to filter out users from destinations they have not consented to during during syncing.",
        lastUpdatedDate: "Tue May 14 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What are destination rules?",
          "depth": 0,
          "id": "what-are-destination-rules"
        }, {
          "value": "Setting up destination rules",
          "depth": 0,
          "id": "setting-up-destination-rules"
        }, {
          "value": "Previewing destination rules",
          "depth": 0,
          "id": "previewing-destination-rules"
        }, {
          "value": "Using destination rules in syncs",
          "depth": 0,
          "id": "using-destination-rules-in-syncs"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      img: "img",
      ol: "ol",
      li: "li",
      a: "a",
      code: "code"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "what-are-destination-rules",
        children: "What are destination rules?"
      }), "\n", _jsx(_components.p, {
        children: "Destination rules are a way for customers to specify which row can get synced to certain destinations.\nIt acts as a governance tool for row-level information. A common use case is to sync users to all destinations\nexcept those they haven't consented to."
      }), "\n", _jsx(_components.h2, {
        id: "setting-up-destination-rules",
        children: "Setting up destination rules"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destination-rules/rules-table.png",
          alt: "Destination rules"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/governance/destination-rules",
            children: "governance page"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Click \"Add rule\""
        }), "\n", _jsx(_components.li, {
          children: "Select a parent model, destination, and filters"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Similar to building an audience, the\nbuilder provides five types of filters you can use\nwith ", _jsxs(_components.a, {
          href: "https://hightouch.com/docs/customer-studio/usage#nested-conditions",
          children: ["nested Boolean (", _jsx(_components.code, {
            children: "AND"
          }), " / ", _jsx(_components.code, {
            children: "OR"
          }), ") logic"]
        }), ":"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destination-rules/rule-definition.png",
          alt: "Destination rule definition"
        })
      }), "\n", _jsx(_components.h2, {
        id: "previewing-destination-rules",
        children: "Previewing destination rules"
      }), "\n", _jsx(_components.p, {
        children: "After creating a destination rule, go to an audience under the same parent model. By selecting the\ndestination rule, the filter will be applied on top of the existing audience conditions. All audience\npreview features will now include the destination rule."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destination-rules/audience-preview.png",
          alt: "Audience preview"
        })
      }), "\n", _jsx(_components.h2, {
        id: "using-destination-rules-in-syncs",
        children: "Using destination rules in syncs"
      }), "\n", _jsx(_components.p, {
        children: "Create a sync with a parent model (proxied by the audience) and destination that has an existing rule.\nRows that don't meet a destination rule will be excluded at sync time. All syncs going forward will automatically have the rule applied.\nNote that these excluded rows are still included in the audience definition, so they remain unaffected when using Customer Studio features that don't involve syncing."
      })]
    });
  }
}
export default MDXContent;
